import * as yup from "yup";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import { getAccountUnpaidInvoicesAsync } from "../../../../../services/invoiceService";
import ReactSelect from "./../../../../../components/common/ReactSelect";
import ReactSelectAsync from "./../../../../../components/common/ReactSelectAsync";
import paymentService from "../../../../../services/paymentService";
import Textbox from "./../../../../../components/common/Textbox";
import Textarea from "../../../../../components/common/Textarea";
import Toaster from "./../../../../../components/common/Toaster";
import Datepicker from "../../../../../components/common/Datepicker";
import moment from "moment";

const schema = yup.object({
  email_send: yup.bool(),
  amount: yup.string().required("Please enter amount."),
  transaction_date: yup.string().required("Please select transaction date."),
  payment_type: yup.object().shape({
    label: yup.string().required("Please select payment type."),
    value: yup.string().required("Please select payment type."),
  }),
  accounting_invoice_id: yup.object().shape({
    label: yup.string().required("Please select invoice."),
    value: yup.string().required("Please select invoice."),
  }),
});

const PaymentForm = ({ id, isLoading, setLoading, onSuccess }) => {
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [transactionDate] = useState(
    Date.parse(moment(new Date(), "MM-DD-yyyy hh:mm a").toISOString())
  );

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    const fetchPaymentTypes = () => {
      const data = paymentService.getPaymentTypes();
      setPaymentTypes(data);
    };

    fetchPaymentTypes();
  }, []);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await paymentService.saveAccountPaymentAsync(id, data);
      Toaster.Success("Payment Saved Successfully");
      onSuccess();
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const options = async (search, loadedOptions, { page }) => {
    const params = {
      search_query: search,
      current_page: page,
      page_size: 10,
      sort_column: { path: "invoice_number", order: "asc" },
    };

    const response = await getAccountUnpaidInvoicesAsync(id, params);
    const { result: data, count, total_count } = response;

    const options = data.map((item) => {
      const container = {};
      container["label"] = item.invoice_number;
      container["value"] = item.id;
      container["amount"] = item.amount;
      container["period_start"] = item.period_start;
      container["period_end"] = item.period_end;

      return container;
    });

    const hasMore = total_count > count;

    return {
      options,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  };

  const handleSelectInvoice = (data) => {
    setValue("amount", data.amount);
    clearErrors("amount");
    setInvoice(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelectAsync
            id="invoices-dropdown"
            name="accounting_invoice_id"
            title="Invoice No."
            options={options}
            control={control}
            onSelectChange={handleSelectInvoice}
            register={register("accounting_invoice_id")}
            errors={errors}
          />
        </div>
        {invoice && (
          <div className="col-span-2 mt-5">
            <div className="card bg-blue-200 border-blue-300 p-3">
              <div>
                <p className="text-blue-600 text-sm">Period:</p>
                <span className="text-sm text-gray-600">
                  {moment(invoice.period_start).format("LL")} to{" "}
                  {moment(invoice.period_end).format("LL")}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="reference-number-textbox"
            name="reference_number"
            type="text"
            title="Reference No."
            register={register("reference_number")}
            errors={errors}
            optional
          />
        </div>
      </div>
      <div className="grid grid-cols-2 space-x-4">
        <div className="col-span-1">
          <ReactSelect
            id="payment-type-dropdown"
            name="payment_type"
            title="Payment Type"
            options={paymentTypes}
            control={control}
            register={register("payment_type")}
            errors={errors}
          />
        </div>
        <div className="col-span-1">
          <Textbox
            id="amount-textbox"
            name="amount"
            type="text"
            title="Amount"
            register={register("amount")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Datepicker
            id="transaction-datepicker"
            name="transaction_date"
            title="Transaction Date"
            dateFormat="MM-DD-yyyy"
            timeFormat="hh:mm a"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("transaction_date")}
            initialValue={transactionDate}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <input
            id="email-send-checkbox"
            name="email_send"
            type="checkbox"
            defaultChecked={true}
            {...register("email_send")}
            className="text-blue-500 cursor-pointer border-gray-300 rounded dark:border-gray-600"
          />
          <label className="ml-3 text-sm text-gray-400">Send via Email</label>
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textarea
            id="remarks-textarea"
            name="remarks"
            title="Remarks"
            placeholder="Say something..."
            register={register("remarks")}
            errors={errors}
            rows={4}
            optional
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default PaymentForm;
